import { useState, useEffect } from "react";
import { db, storage } from "../../../firebase";
import { addDoc, collection, getDocs } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import  AddCategory  from "../productsPage/AddCategory";

const AddMachinePage = () => {
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    specification: ""
  });
  const [imageFile, setImageFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Load categories
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const snapshot = await getDocs(collection(db, "categories"));
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category) {
      setMessage("Please fill machine name and category");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      let imageUrl = "";
      let pdfUrl = "";

      // Upload files
      if (imageFile) {
        const category = categories.find(cat => cat.id === formData.category);
        const folderName = category?.name.toLowerCase().replace(/\s+/g, '-');
        imageUrl = await uploadFile(imageFile, `images/${folderName}/${Date.now()}_${imageFile.name}`);
      }

      if (pdfFile) {
        const category = categories.find(cat => cat.id === formData.category);
        const folderName = category?.name.toLowerCase().replace(/\s+/g, '-');
        pdfUrl = await uploadFile(pdfFile, `pdfs/${folderName}/${Date.now()}_${pdfFile.name}`);
      }

      // Save to category subcollection
      await addDoc(collection(db, "categories", formData.category, "machines"), {
        name: formData.name,
        description: formData.description,
        specification: formData.specification,
        image: imageUrl,
        pdf: pdfUrl,
        createdAt: new Date()
      });

      setMessage("✅ Machine added successfully!");
      resetForm();

    } catch (error) {
      console.error("Error:", error);
      setMessage("❌ Error: " + error.message);
    }
    setLoading(false);
  };

  const uploadFile = async (file, path) => {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const resetForm = () => {
    setFormData({ name: "", category: "", description: "", specification: "" });
    setImageFile(null);
    setPdfFile(null);
    document.getElementById("imageInput").value = "";
    document.getElementById("pdfInput").value = "";
  };

  return (
    <>
    <AddCategory />
        <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Add New Machine</h1>
          <p className="text-gray-600 mt-2">Add machinery details to your catalog</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Machine Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Machine Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Enter machine name"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Select Category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {categories.length === 0 && (
                <p className="text-red-600 text-sm mt-2">
                  No categories found. Please create categories first.
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                placeholder="Describe the machine features and benefits..."
              />
            </div>

            {/* Specification */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specification
              </label>
              <textarea
                rows={3}
                value={formData.specification}
                onChange={(e) => setFormData(prev => ({ ...prev, specification: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                placeholder="Enter machine specifications..."
              />
            </div>

            {/* File Uploads */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Machine Image
                </label>
                <input
                  id="imageInput"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0])}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {imageFile && (
                  <p className="text-green-600 text-sm mt-2">
                    ✅ {imageFile.name}
                  </p>
                )}
              </div>

              {/* PDF Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catalog PDF
                </label>
                <input
                  id="pdfInput"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {pdfFile && (
                  <p className="text-green-600 text-sm mt-2">
                    ✅ {pdfFile.name}
                  </p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || categories.length === 0}
              className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Adding Machine...
                </div>
              ) : (
                "➕ Add Machine"
              )}
            </button>

            {/* Message */}
            {message && (
              <div className={`p-4 rounded-lg ${
                message.includes('✅') 
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                {message}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default AddMachinePage;